import React, { useState, useEffect, useCallback, useRef } from "react";
import "./FlashRecall.css";
import overlay from "../../assets/img/overlay.png";
import brainlogo from "../../assets/img/brainlogo.png";
import ResultPage from "../../Components/Games/ResultPage";
import GameIntroScreen from "../../Components/Games/GameIntroScreen";
import StartGame from "../../Components/Games/StartGame"; // Assuming StartGame.jsx is the HealthBenefitsScreen
import TopBar from "../../Components/Games/TopBar";
import { lifestyleApi } from '../../apiService/api';
import { getUrlParams } from '../../apiService/timespam';
import { useGameBackNavigation } from "../../Components/Games/useGameBackNavigation";

// --- GAME CONSTANTS ---
const GAME_CONSTANTS = {
    GAME_DURATION: 60, // seconds
    GRID_SIZE: 16, // 4x4
    MEMORIZE_DURATION: 1500, // ms
    MEMORIZE_INTERVAL: 50, // ms update frequency for smooth slider
    LEVEL_INTERVAL: 10, // seconds per difficulty bump

    EMOJIS: [
        '🍎', '🍌', '🍇', '🍉', '🍓', '🍒', '🍑', '🍍',
        '🥝', '🥑', '🍆', '🥕', '🌽', '🌶️', '🥦', '🍄',
        '🍞', '🧀', '🍖', '🍗', '🍔', '🍟', '🍕', '🌭'
    ],

    MOTIVATIONAL_PHRASES: [
        "Keep going!", "Amazing!", "So fast!", "Unstoppable!", "Laser Focus!",
        "In the Zone!", "Brilliant!", "Don't Stop!"
    ],

    URGENCY_PHRASES: [
        "Hurry up!", "Time is ticking!", "Focus now!", "Last seconds!", "Move move move!"
    ],

    GameState: {
        IDLE: 'IDLE',
        MEMORIZING: 'MEMORIZING',
        GUESSING: 'GUESSING',
        GAME_OVER: 'GAME_OVER',
    }
};

// --- GridCell Component ---
const GridCell = ({
    index, icon, isRevealed, isSelected, isWrong, isTarget, onClick, disabled
}) => {
    let statusClass = "";
    let content = null;

    if (isRevealed && isTarget) { statusClass = "flashrecall-cell-revealed"; content = icon; }
    else if (isSelected && isTarget) { statusClass = "flashrecall-cell-correct"; content = icon; }
    else if (isWrong) { statusClass = "flashrecall-cell-wrong"; content = "❌"; }

    return (
        <button
            onClick={() => onClick(index)}
            disabled={disabled}
            className={`flashrecall-cell ${statusClass}`}
            type="button"
        >
            {content}
        </button>
    );
};

// --- FlashRecall Component (Manages all screens) ---
const FlashRecall2 = () => {
    useGameBackNavigation();

    // --- APP FLOW STATE: Uses string literals for screen phase ---
    const [appPhase, setAppPhase] = useState('INTRO');

    // --- GAME STATE ---
    const [gameState, setGameState] = useState(GAME_CONSTANTS.GameState.IDLE);
    const [timeLeft, setTimeLeft] = useState(GAME_CONSTANTS.GAME_DURATION);
    const [memorizeTimeRemaining, setMemorizeTimeRemaining] = useState(0);
    const [gridItems, setGridItems] = useState([]);
    const [selectedIndices, setSelectedIndices] = useState(new Set());
    const [wrongIndices, setWrongIndices] = useState(new Set());

    // --- TUTORIAL ---
    const [tutorialStep, setTutorialStep] = useState(0);

    // --- STATS STATE ---
    const [stats, setStats] = useState({ score: 0, level: 1, streak: 0, bestScore: 0 });
    const [feedback, setFeedback] = useState("Ready?");
    const [difficultyLevel, setDifficultyLevel] = useState(2);

    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [wrongAnswers, setWrongAnswers] = useState(0);
    const [maxStreak, setMaxStreak] = useState(0);
    const [fastResponses, setFastResponses] = useState(0);
    const [cid, setCid] = useState("");
    const [groupName, setGroupName] = useState("");
    const [gameStateApi, setGameStateApi] = useState("initial");
    const [lastInsertedId, setLastInsertedId] = useState(null);
    const [goqiiBest, setGoqiiBest] = useState(0);
    const [backendCognitiveScore, setBackendCognitiveScore] = useState(null);
    const [backendSkill, setBackendSkill] = useState("");
    const [weightages, setWeightages] = useState({ attemptWeightage: 1, accuracyWeightage: 1, streakWeightage: 1, fastWeightage: 1, scoreWeightage: 1, baseQuestionsAttempt: 0, correctMinQuestion: 1, streakMinQuestion: 1, fastResponseMinQuestion: 1, scorePoints: 1 });
    const [configError, setConfigError] = useState(false);
    const guessingStartTimeRef = useRef(null);


    const gameStartTimeRef = useRef(0);
    const timerIntervalRef = useRef(null);
    const memorizeTimerRef = useRef(null);
    const lastInsertedIdRef = useRef(null);


    // --- TUTORIAL DATA ---
    const flashRecallTutorialSteps = [
        {
            icon: "👀",
            title: "MEMORIZE",
            desc: "Look carefully at the highlighted cells"
        },
        {
            icon: "🧠",
            title: "REMEMBER",
            desc: "Try to remember the positions"
        },
        {
            icon: "👇",
            title: "TAP",
            desc: "Tap the same cells after they hide"
        },
        {
            icon: "⚡",
            title: "BE FAST",
            desc: "Time is limited so react quickly"
        }
    ];


    // --- APP DATA (Placeholder Configuration) ---
    const appData = {
        backgroundImage: overlay,
        gameLogo: brainlogo,
        title: "Flash Recall",
        goqiiBest: goqiiBest,
        hbTitle: "Cognitive Benefits",
        hbHeading: "Improves Working Memory",
        hbDescription: "Memorize the objects and strengthen your short-term memory and focus.",
    };


    // --- APP NAVIGATION HANDLERS ---

    // INTRO -> BENEFITS
    const handleIntroStart = () => {
        setAppPhase('BENEFITS');
    };

    // BENEFITS -> GAME
    const handleBenefitsStart = async () => {
        const { encryptedId, gid } = getUrlParams();

        try {
            const res = await lifestyleApi.initialGameScore(encryptedId, gid);

            if (res.data?.code === 200) {
                const data = res.data.data;

                setCid(data.cid);
                setGroupName(data.groupName);
                setLastInsertedId(data.lastInsertedId);
                lastInsertedIdRef.current = res.data.data.lastInsertedId;
                setGameStateApi("initial");
            }
        } catch (err) {
            console.error("initialGameScore failed", err);
        }
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        if (memorizeTimerRef.current) clearInterval(memorizeTimerRef.current);
        setGameState(GAME_CONSTANTS.GameState.IDLE);

        setAppPhase('TUTORIAL');
    };

    const fetchGameConfig = () => {
        setConfigError(false);
        const { encryptedId, gid } = getUrlParams();

        lifestyleApi.fetchGameDetails(encryptedId, gid)
            .then(res => {
                if (res.data?.code === 200) {
                    const d = res.data.data;
                    setStats(prev => ({ ...prev, bestScore: d.bestScore }));
                    setGoqiiBest(d.goqiiBest);
                    setWeightages({
                        attemptWeightage: d.attemptWeightage ?? 1,
                        accuracyWeightage: d.accuracyWeightage ?? 1,
                        streakWeightage: d.streakWeightage ?? 1,
                        fastWeightage: d.fastWeightage ?? 1,
                        scoreWeightage: d.scoreWeightage ?? 1,
                        baseQuestionsAttempt: d.baseQuestionsAttempt || 0,
                        correctMinQuestion: d.correctMinQuestion || 1,
                        streakMinQuestion: d.streakMinQuestion || 1,
                        fastResponseMinQuestion: d.fastResponseMinQuestion || 1,
                        scorePoints: d.scorePoints || 1,
                    });
                } else {
                    setConfigError(true);
                }
            })
            .catch(err => {
                console.error("fetchGameDetails failed", err);
                setConfigError(true);
            });
    };

    useEffect(() => {
        fetchGameConfig();
    }, []);


    // REPLAY (RESULT -> BENEFITS)
    const handleReplayClick = useCallback(() => {
        setGameState(GAME_CONSTANTS.GameState.IDLE); // Reset game visibility
        setAppPhase('TUTORIAL'); // Go back to Benefits screen
    }, []);

    const handleShare = () => {
        alert('Sharing function not yet implemented.');
    };

    // --- GAME CONTROL FUNCTIONS ---

    const generateLevel = useCallback((numTargets) => {
        if (memorizeTimerRef.current) clearInterval(memorizeTimerRef.current);

        const newItems = Array.from({ length: GAME_CONSTANTS.GRID_SIZE }, (_, i) => ({
            id: i, icon: null, isTarget: false,
        }));

        const indices = Array.from({ length: GAME_CONSTANTS.GRID_SIZE }, (_, i) => i);
        const shuffledIndices = indices.sort(() => Math.random() - 0.5);
        const targetIndices = shuffledIndices.slice(0, numTargets);

        targetIndices.forEach((index) => {
            newItems[index].isTarget = true;
            newItems[index].icon = GAME_CONSTANTS.EMOJIS[Math.floor(Math.random() * GAME_CONSTANTS.EMOJIS.length)];
        });

        setGridItems(newItems);
        setSelectedIndices(new Set());
        setWrongIndices(new Set());
        setGameState(GAME_CONSTANTS.GameState.MEMORIZING);

        setMemorizeTimeRemaining(GAME_CONSTANTS.MEMORIZE_DURATION);

        let timeElapsed = 0;

        memorizeTimerRef.current = setInterval(() => {
            timeElapsed += GAME_CONSTANTS.MEMORIZE_INTERVAL;
            const remaining = GAME_CONSTANTS.MEMORIZE_DURATION - timeElapsed;

            if (remaining <= 0) {
                clearInterval(memorizeTimerRef.current);
                setMemorizeTimeRemaining(0);

                  guessingStartTimeRef.current = Date.now(); 

                setGameState((prev) => {
                    if (prev === GAME_CONSTANTS.GameState.GAME_OVER) return prev;
                    return GAME_CONSTANTS.GameState.GUESSING;
                });
            } else {
                setMemorizeTimeRemaining(remaining);
            }
        }, GAME_CONSTANTS.MEMORIZE_INTERVAL);
    }, []);


    const startGameFlow = useCallback(() => {
        // 1. Reset variables
        setStats(prev => ({ ...prev, score: 0, level: 1, streak: 0 }));
        setCorrectAnswers(0);
        setWrongAnswers(0);
        setMaxStreak(0);
        setFastResponses(0);
        setTimeLeft(GAME_CONSTANTS.GAME_DURATION);
        setFeedback("Let's go!");
        setDifficultyLevel(2);

        gameStartTimeRef.current = Date.now();

        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        if (memorizeTimerRef.current) clearInterval(memorizeTimerRef.current);

        // 2. Start Main Timer
        timerIntervalRef.current = window.setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    endGame();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        // 3. Start first level and transition to MEMORIZING
        generateLevel(2);
        setAppPhase('GAME'); // Set phase to 'GAME'
    }, [generateLevel]);




    // --- EFFECTS & GAME LOGIC ---

    useEffect(() => {
        // Cleanup timers on component unmount
        return () => {
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
            if (memorizeTimerRef.current) clearInterval(memorizeTimerRef.current);
        };
    }, []);

    useEffect(() => {
        // Feedback update logic
        if (gameState === GAME_CONSTANTS.GameState.GAME_OVER) return;

        if (gameState === GAME_CONSTANTS.GameState.IDLE) {
            setFeedback("Ready?");
        } else if (timeLeft < 15 && timeLeft > 0) {
            setFeedback(GAME_CONSTANTS.URGENCY_PHRASES[Math.floor(Math.random() * GAME_CONSTANTS.URGENCY_PHRASES.length)]);
        } else if (stats.streak > 2) {
            setFeedback(GAME_CONSTANTS.MOTIVATIONAL_PHRASES[Math.floor(Math.random() * GAME_CONSTANTS.MOTIVATIONAL_PHRASES.length)]);
        }
    }, [timeLeft, stats.streak, gameState]);


    const handleCellClick = (index) => {
        if (gameState !== GAME_CONSTANTS.GameState.GUESSING) return;
        if (selectedIndices.has(index) || wrongIndices.has(index)) return;

        const item = gridItems[index];

        if (item.isTarget) {
            const newSelected = new Set(selectedIndices);
            newSelected.add(index);
            setSelectedIndices(newSelected);

            setCorrectAnswers(prev => prev + 1);
            const streakBeforeThisAction = stats.streak;
            const newStreak = streakBeforeThisAction + 1;
            setMaxStreak(prev => Math.max(prev, newStreak));

            const totalTargets = gridItems.filter(i => i.isTarget).length;
            const completesLevel = newSelected.size === totalTargets;
            // handleLevelComplete determines (and returns) whether this level
            // finished within the "fast" threshold, so the timing check lives
            // in exactly one place instead of being duplicated here.
            const isFast = completesLevel ? handleLevelComplete() : false;

            const streakBonus = weightages.streakWeightage * 10 * streakBeforeThisAction;
            const fastBonus = isFast ? weightages.fastWeightage * 10 : 0;
            const gained = weightages.scorePoints + streakBonus + fastBonus;

            setStats(prev => ({
                ...prev,
                streak: newStreak,
                score: Math.max(0, prev.score + gained),
            }));
        } else {
            const newWrong = new Set(wrongIndices);
            newWrong.add(index);
            setWrongIndices(newWrong);

            setWrongAnswers(prev => prev + 1);
            setStats(prev => ({
                ...prev,
                streak: 0,
                score: Math.max(0, prev.score - weightages.scorePoints / 2),
            }));

            setFeedback("Oops! Focus!");
        }
    };

    const handleLevelComplete = () => {
        const now = Date.now();
        let isFast = false;

        // --- FAST RESPONSE LOGIC START ---
    if (guessingStartTimeRef.current) {
        const timeTakenMs = now - guessingStartTimeRef.current;
        const timeTakenSec = timeTakenMs / 1000;

        // Threshold: 0.8 seconds per target cell (aap ise adjust kar sakte hain)
        // Level 2 (2 targets) = 1.6s se kam fast hai
        // Level 5 (5 targets) = 4.0s se kam fast hai
        const fastThreshold = difficultyLevel * 0.8;

        if (timeTakenSec < fastThreshold) {
            setFastResponses(prev => prev + 1);
            isFast = true;
            // setFeedback("⚡ Lightning Fast! ⚡");
        }
    }
    // --- FAST RESPONSE LOGIC END ---

        const elapsedSeconds = (now - gameStartTimeRef.current) / 1000;

        const timeBasedDifficulty = Math.floor(elapsedSeconds / GAME_CONSTANTS.LEVEL_INTERVAL) + 2;

        let nextDifficulty = difficultyLevel;

        if (timeBasedDifficulty > difficultyLevel) {
            nextDifficulty = Math.min(difficultyLevel + 1, 8);
        }

        setDifficultyLevel(nextDifficulty);

        setStats(prev => ({
            ...prev,
            level: nextDifficulty - 1,
        }));

        setTimeout(() => {
            if (gameState !== GAME_CONSTANTS.GameState.GAME_OVER) {
                generateLevel(nextDifficulty);
            }
        }, 500);

        return isFast;
    };

    const getDifficultyStars = () => {
        return "⭐".repeat(Math.max(1, difficultyLevel - 1));
    };



    const getProgressBarProps = () => {
        if (gameState === GAME_CONSTANTS.GameState.MEMORIZING) {
            const percentage = (memorizeTimeRemaining / GAME_CONSTANTS.MEMORIZE_DURATION) * 100;
            return { width: `${percentage}%`, className: 'flashrecall-progress-normal' };
        }

        if (gameState === GAME_CONSTANTS.GameState.GUESSING) {
            const percentage = (timeLeft / GAME_CONSTANTS.GAME_DURATION) * 100;
            const isUrgent = timeLeft < 10 && timeLeft > 0;
            return {
                width: `${percentage}%`,
                className: isUrgent
                    ? 'flashrecall-progress-urgent'
                    : 'flashrecall-progress-normal'
            };
        }

        return { width: '0%', className: 'flashrecall-progress-normal' };
    };
    const progressBarProps = getProgressBarProps();

    const skipMemorize = useCallback(() => {
        if (gameState !== GAME_CONSTANTS.GameState.MEMORIZING) return;

        if (memorizeTimerRef.current) {
            clearInterval(memorizeTimerRef.current);
            memorizeTimerRef.current = null;
        }

        setMemorizeTimeRemaining(0);
        guessingStartTimeRef.current = Date.now(); 

        setGameState(prev => {
            if (prev === GAME_CONSTANTS.GameState.GAME_OVER) return prev;
            return GAME_CONSTANTS.GameState.GUESSING;
        });
    }, [gameState]);


    const saveFinalGameScore = async () => {
        const { encryptedId, gid } = getUrlParams();
        const totalQuestions = correctAnswers + wrongAnswers;

        const payload = {
            goqiiUserId: encryptedId,
            gid,
            totalQuestions,
            correctAnswers,
            wrongAnswers,
            longestStreak: maxStreak,
            fastResponse: fastResponses,
            score: stats.score,
            cid,
            lastInsertedId: lastInsertedIdRef.current,
        };

        if (!payload.lastInsertedId) {
            console.warn("Skipping saveGameScore: lastInsertedId missing");
            return;
        }

        try {
            const res = await lifestyleApi.saveGameScore(
                payload.goqiiUserId,
                payload.gid,
                payload.totalQuestions,
                payload.correctAnswers,
                payload.wrongAnswers,
                payload.longestStreak,
                payload.fastResponse,
                payload.score,
                payload.cid,
                payload.lastInsertedId
            );

            if (res?.data?.data?.cognitiveScore !== undefined) {
                setBackendCognitiveScore(res.data.data.cognitiveScore);
            }
            if (res?.data?.data?.skill) {
                setBackendSkill(res.data.data.skill);
            }
        } catch (err) {
            console.error("saveGameScore failed", err);
        }
    };




    const endGame = useCallback(() => {
        setStats(prev => ({
            ...prev,
            bestScore: Math.max(prev.bestScore, prev.score)
        }));

        setGameState(GAME_CONSTANTS.GameState.GAME_OVER);
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        if (memorizeTimerRef.current) clearInterval(memorizeTimerRef.current);
        setAppPhase('RESULT'); // Set phase to 'RESULT'
    }, [stats.score, stats.bestScore]);


    useEffect(() => {
        if (gameState === GAME_CONSTANTS.GameState.GAME_OVER) {
            if (lastInsertedIdRef.current) {
                saveFinalGameScore();
            } else {
                console.warn("Score NOT saved: lastInsertedId missing");
            }
        }
    }, [gameState]);

    const getQuote = () => {
        const total = correctAnswers + wrongAnswers;
        const accuracy = total > 0 ? Math.round((correctAnswers / total) * 100) : 0;

        if (backendCognitiveScore >= 90 || (accuracy >= 95 && maxStreak >= 8))
            return "Photographic memory! You recall patterns at lightning speed.";
        if (backendCognitiveScore >= 75 || (accuracy >= 85 && maxStreak >= 5))
            return "Sharp recall! Your working memory is strong and fast.";
        if (backendCognitiveScore >= 60 || accuracy >= 70)
            return "Good memory control. Your recall is improving.";
        if (backendCognitiveScore >= 40)
            return "Nice effort. Train more to boost memory speed.";
        return "Every round strengthens your memory. Keep practicing!";
    };




    // BLOCKING SERVER ERROR MODAL (takes over regardless of appPhase)
    if (configError) {
        return (
            <div className="flashrecall flashrecall-app-container">
                <div className="api-error-overlay">
                    <div className="api-error-card">
                        <div className="api-error-title">Something went wrong</div>
                        <div className="api-error-text">There is a server error to open this game. Please try again after some time.</div>
                        <button className="api-error-retry-btn" onClick={fetchGameConfig}>Retry</button>
                    </div>
                </div>
            </div>
        );
    }

    // 1. INTRO SCREEN
    if (appPhase === 'INTRO') {
        return (
            <GameIntroScreen
                backgroundImage={appData.backgroundImage}
                gameLogo={appData.gameLogo}
                title={appData.title}
                onStart={handleIntroStart}
            />
        );
    }

    // 2. BENEFITS SCREEN (Health Benefits / Start Game)
    if (appPhase === 'BENEFITS') {
        return (
            <StartGame
                backgroundImage={appData.backgroundImage}
                onStart={handleBenefitsStart}
                yourBest={stats.bestScore}
                goqiiBest={appData.goqiiBest}
                hbTitle={appData.hbTitle}
                hbHeading={appData.hbHeading}
                hbDescription={appData.hbDescription}
            />
        );
    }

    if (appPhase === 'TUTORIAL') {
        const step = flashRecallTutorialSteps[tutorialStep];

        return (
            <div className="flashrecall flashrecall-app-container">

                <div className="flashrecall-tutorial-frame">

                    {/* Header */}
                    <div className="flashrecall-tutorial-header">

                        <div className="flashrecall-tutorial-label">
                            TUTORIAL
                        </div>

                        <button
                            className="flashrecall-tutorial-skip"
                            onClick={() => {
                                setAppPhase('STARTING');
                                setTimeout(() => startGameFlow(), 1200);
                            }}
                        >
                            Skip ▶
                        </button>

                    </div>

                    {/* Instruction Box */}
                    <div className="flashrecall-tutorial-instruction">
                        Step {tutorialStep + 1}: {step.title}
                    </div>

                    {/* Icon */}
                    <div className="flashrecall-tutorial-icon">
                        {step.icon}
                    </div>

                    {/* Description */}
                    <p className="flashrecall-tutorial-desc">
                        {step.desc}
                    </p>

                    {/* Buttons */}
                    {tutorialStep < flashRecallTutorialSteps.length - 1 ? (
                        <button
                            className="flashrecall-tutorial-btn"
                            onClick={() => setTutorialStep(tutorialStep + 1)}
                        >
                            Next
                        </button>
                    ) : (
                        <button
                            className="flashrecall-tutorial-btn"
                            onClick={() => {
                                setAppPhase('STARTING');
                                setTimeout(() => startGameFlow(), 1200);
                            }}
                        >
                            Start Game
                        </button>
                    )}

                </div>

            </div>
        );
    }

    if (appPhase === 'STARTING') {
        return (
            <div className="flashrecall flashrecall-app-container">
                <div className="flashrecall-start-overlay">
                    <div className="flashrecall-start-text">START!</div>
                </div>
            </div>
        );
    }

    // 3. RESULT SCREEN
    if (appPhase === 'RESULT') {
        return (
            <ResultPage
                visible={true}
                score={stats.score}
                best={stats.bestScore}
                correct={correctAnswers}
                wrong={wrongAnswers}
                longestStreak={maxStreak}
                fastResponses={fastResponses}
                cognitiveScore={backendCognitiveScore}
                domainLabel={backendSkill}
                quote={getQuote()}
                onReplay={handleReplayClick}// Navigates back to BENEFITS screen
                onShare={handleShare}
            />
        );
    }

    // Default: APP_PHASE.GAME (Active Game UI)
    return (
        <div className="flashrecall flashrecall-app-container">
            {/* Status Bar */}
            <TopBar
                score={stats.score}
                timeLeft={timeLeft}
            />
            <div className="flashrecall-game-wrapper">


                {/* Feedback */}
                <div className="flashrecall-status-feedback">
                    {feedback}
                </div>

                {/* Progress Bar (Visible during MEMORIZING or GUESSING) */}
                {gameState === GAME_CONSTANTS.GameState.MEMORIZING || gameState === GAME_CONSTANTS.GameState.GUESSING ? (
                    <div className="flashrecall-progress-container">
                        <div
                            className={`flashrecall-progress-bar ${progressBarProps.className}`}
                            style={{
                                width: progressBarProps.width,
                                transition: gameState === GAME_CONSTANTS.GameState.MEMORIZING ? 'width 0.05s linear' : 'width 1s linear, background-color 0.3s'
                            }}
                        />
                    </div>
                ) : null}
                {/* Game Board */}
                {gameState === GAME_CONSTANTS.GameState.MEMORIZING && (
                    <div className="flashrecall-skip-hint">
                        Tap anywhere to start early
                    </div>
                )}
                <div className="flashrecall-game-board">
                    {/* Only render grid when memorizing or guessing */}
                    {gameState === GAME_CONSTANTS.GameState.MEMORIZING || gameState === GAME_CONSTANTS.GameState.GUESSING ? (
                        <div className="flashrecall-grid"
                            onClick={skipMemorize}
                            onTouchStart={skipMemorize}>
                            {gridItems.map((item, idx) => (
                                <GridCell
                                    key={`${item.id}-${stats.streak}`}
                                    index={idx}
                                    icon={item.icon}
                                    isTarget={item.isTarget}
                                    isRevealed={gameState === GAME_CONSTANTS.GameState.MEMORIZING}
                                    isSelected={selectedIndices.has(idx)}
                                    isWrong={wrongIndices.has(idx)}
                                    onClick={handleCellClick}
                                    disabled={gameState !== GAME_CONSTANTS.GameState.GUESSING}
                                />
                            ))}
                        </div>
                    ) : (
                        null
                    )}
                </div>

                {/* Difficulty/Level in the footer area */}
                {gameState !== GAME_CONSTANTS.GameState.IDLE && gameState !== GAME_CONSTANTS.GameState.GAME_OVER && (
                    <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                            Level:
                        </span>
                        <span className="stars" style={{ marginLeft: '0.5rem', fontWeight: 'bold' }}>
                            {getDifficultyStars()}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FlashRecall2;